<template>
  <div class="task-panel">
    <PlatformPanelLoading v-if="loading" label="正在同步设计任务数据…" />
    <header class="task-page-header">
      <div class="header-main">
        <div class="header-title-row">
          <h1>我的任务</h1>
        </div>
        <p class="header-sub">订单设计、状态流转与排产交付统一工作台</p>
      </div>
    </header>

    <div class="task-command-row">
      <TaskOverviewCard :stats="taskStats" />
      <TaskFilters v-model="filters" :stores="accessibleStores" @search="search" @reset="resetFilters" />
    </div>

    <section class="task-table-card">
      <div class="task-list-header">
        <div class="list-title-box">
          <div class="title-with-bar">
            <h2>我的任务列表</h2>
            <span class="count-pill">共 <strong>{{ total }}</strong> 项任务</span>
          </div>
        </div>
        <div class="list-header-actions">
          <button type="button" class="btn-refresh" :disabled="loading" @click="loadTasks">
            <RefreshCw :size="13" :class="{ spinning: loading }" />
            <span>刷新</span>
          </button>
          <div class="claim-group">
            <button type="button" class="btn-claim" @click="claimTasks(1)">
              <PlusCircle :size="13" />
              <span>快速接单 (1单)</span>
            </button>
            <button type="button" class="btn-claim-bulk" @click="claimTasks(5)">
              <Sparkles :size="13" />
              <span>批量接单 (5单)</span>
            </button>
          </div>
        </div>
      </div>

      <div v-if="errorMessage" class="task-alert">
        <AlertCircle :size="15" />
        <span>{{ errorMessage }}</span>
      </div>

      <div class="task-table-scroll">
        <table class="task-table">
          <thead>
            <tr>
              <th>订单编号</th>
              <th>客户旺旺 / 名称</th>
              <th>下单时间</th>
              <th>任务创建时间</th>
              <th>店铺</th>
              <th>客服</th>
              <th>产品分类</th>
              <th>设计师</th>
              <th>设计状态</th>
              <th class="col-actions">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading && !tasks.length">
              <td colspan="10" class="empty-row">
                <div class="empty-state-box">
                  <div class="table-spinner"></div>
                  <p>正在同步设计任务数据…</p>
                </div>
              </td>
            </tr>
            <tr v-else-if="!tasks.length">
              <td colspan="10" class="empty-row">
                <WhaleEmpty
                  compact
                  size="small"
                  variant="search"
                  title="当前筛选条件下暂无设计任务"
                  description="您可以重置筛选条件或点击右上角「快速接单」获取新订单"
                  action-text="重置筛选"
                  @action="resetFilters"
                />
              </td>
            </tr>
            <tr
              v-for="task in tasks"
              :key="task.id"
              class="task-table-row"
              @click="detailTask = task"
            >
              <td class="cell-order">
                <div class="order-no-wrapper">
                  <span class="order-number">{{ task.orderNo }}</span>
                </div>
              </td>
              <td class="cell-customer">
                <div class="customer-info">
                  <span class="customer-name">{{ task.customerDisplay }}</span>
                </div>
              </td>
              <td class="cell-time">{{ task.orderTime }}</td>
              <td class="cell-time">{{ task.taskCreatedTime || '-' }}</td>
              <td class="cell-store">
                <span class="store-badge">{{ task.storeName }}</span>
              </td>
              <td class="cell-service">{{ task.serviceName }}</td>
              <td class="cell-category">
                <span class="category-tag">{{ task.productCategory }}</span>
              </td>
              <td class="cell-designer">{{ task.designerName || '-' }}</td>
              <td class="cell-status">
                <span class="task-status-pill" :class="`tone-${statusInfo(task.status).tone}`">
                  <span class="status-pill-dot"></span>
                  {{ statusInfo(task.status).label }}
                </span>
              </td>
              <td class="cell-actions" @click.stop>
                <div class="row-actions-group">
                  <button
                    type="button"
                    class="btn-act-primary"
                    :disabled="!canStart(task.status) || openingTaskId === task.taskId"
                    @click="startDesign(task)"
                  >
                    <PlayCircle :size="13" />
                    <span>{{ openingTaskId === task.taskId ? '读取 SKU…' : task.status === 'DESIGNING' ? '继续设计' : '开始设计' }}</span>
                  </button>
                  <button
                    type="button"
                    class="btn-act-ghost"
                    :disabled="!canReturn(task.status)"
                    @click="returnTask = task"
                  >
                    <Undo2 :size="13" />
                    <span>退回销售</span>
                  </button>
                  <button
                    type="button"
                    class="btn-act-ghost"
                    :disabled="isTaskDownloading(task.taskId)"
                    @click="downloadMaterials(task)"
                  >
                    <Download :size="13" />
                    <span>{{ isTaskDownloading(task.taskId) ? '下载中…' : '下载素材' }}</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 现代化精致底部分页条 -->
      <PlatformPagination
        v-model:current-page="pageNum"
        v-model:page-size="pageSize"
        :total="total"
        item-name="项任务"
        @change="loadTasks"
      />
    </section>

    <TaskDetailModal :task="detailTask" @close="detailTask = null" @start="startDesign" />
    <ReturnSalesDialog :task="returnTask" :submitting="returning" @close="returnTask = null" @confirm="confirmReturn" />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import {
  AlertCircle,
  Download,
  PlayCircle,
  PlusCircle,
  RefreshCw,
  Sparkles,
  Undo2,
} from '@lucide/vue';
import { usePlatformStore } from '@/composables/usePlatformStore';
import TaskFilters from '@/components/platform/TaskFilters.vue';
import TaskDetailModal from '@/components/platform/TaskDetailModal.vue';
import ReturnSalesDialog from '@/components/platform/ReturnSalesDialog.vue';
import TaskOverviewCard from '@/components/platform/TaskOverviewCard.vue';
import PlatformPanelLoading from '@/components/platform/PlatformPanelLoading.vue';
import { PlatformPagination, WhaleEmpty } from '@/components/common';
import { DESIGNER_TASK_STATUS, canReturnDesignerTask, canStartDesignerTask, designerTaskService, designerTaskStatusMeta, normalizeDesignerTask } from '@/services/designerTaskService';

const { accessibleStores, session, storeTasks, startDesignFromTask, showToast } = usePlatformStore();
const tasks = ref([]); const total = ref(0); const pageNum = ref(1); const pageSize = ref(10);
const jumpPageInput = ref('');
const loading = ref(false); const returning = ref(false); const sourceMode = ref('local'); const errorMessage = ref('');
const detailTask = ref(null); const returnTask = ref(null);
const downloadingTaskIds = ref(new Set()); const openingTaskId = ref(null);
const taskStats = ref({ completed: 0, pending: 0, designing: 0, returned: 0, returnedSales: 0, refunded: 0, total: 0, completedCurrentMonth: 0 });
const defaultFilters = () => ({ orderNo: '', customerName: '', customerWw: '', customerPhone: '', filename: '', shopName: '', productType: '', designStatus: '' });
const filters = ref(defaultFilters());
const totalPages = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)));

function statusInfo(status) { return designerTaskStatusMeta[status] || { label: status || '未知状态', tone: 'muted' }; }
function canStart(status) { return canStartDesignerTask(status); }
function canReturn(status) { return canReturnDesignerTask(status); }
function getTaskKey(taskId) { return String(taskId ?? ''); }
function isTaskDownloading(taskId) { return downloadingTaskIds.value.has(getTaskKey(taskId)); }

async function loadStats() {
  try {
    const payload = await designerTaskService.getTaskCount();
    const result = payload?.result || {};
    taskStats.value = {
      completed: result.completedCount || 0,
      pending: result.pendingCount || 0,
      designing: result.designingCount || 0,
      returned: result.returnedByFactoryCount || 0,
      returnedSales: result.returnedToSalesCount || 0,
      refunded: result.refundedCount || 0,
      total: result.totalCount || 0,
      completedCurrentMonth: result.completedCountCurrentMonth || 0,
    };
  } catch (err) {
    if (import.meta.env.DEV) console.warn('[task-api] 任务统计加载失败', err);
    taskStats.value = {
      completed: 0,
      pending: 0,
      designing: 0,
      returned: 0,
      returnedSales: 0,
      refunded: 0,
      total: 0,
      completedCurrentMonth: 0,
    };
  }
}

async function loadTasks() {
  loading.value = true;
  errorMessage.value = '';
  try {
    const payload = await designerTaskService.getTaskList({
      pageNum: pageNum.value,
      pageSize: pageSize.value,
      ...filters.value,
      onlyMyself: true,
      orders: [{ column: 'dt.created_time', asc: false }],
    });
    const remoteTasks = (Array.isArray(payload.result) ? payload.result : []).map(normalizeDesignerTask);
    tasks.value = remoteTasks;
    total.value = payload.totalElements ?? remoteTasks.length;
  } catch (error) {
    tasks.value = [];
    total.value = 0;
    if (import.meta.env.DEV) console.warn('[task-api] 远程任务加载失败', error);
    errorMessage.value = error.message || '任务列表加载失败，请稍后重试';
  } finally {
    loading.value = false;
    await loadStats();
  }
}

const visiblePages = computed(() => {
  const current = pageNum.value;
  const max = totalPages.value;
  if (max <= 7) {
    return Array.from({ length: max }, (_, i) => i + 1);
  }
  const pages = [];
  pages.push(1);

  let start = Math.max(2, current - 2);
  let end = Math.min(max - 1, current + 2);

  if (current <= 4) {
    start = 2;
    end = 5;
  } else if (current >= max - 3) {
    start = max - 4;
    end = max - 1;
  }

  if (start > 2) {
    pages.push('...');
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (end < max - 1) {
    pages.push('...');
  }

  pages.push(max);
  return pages;
});

function handleJumpPage() {
  const target = parseInt(jumpPageInput.value, 10);
  if (!isNaN(target) && target >= 1 && target <= totalPages.value) {
    changePage(target);
    jumpPageInput.value = '';
  }
}

function onPageSizeChange() {
  pageNum.value = 1;
  loadTasks();
}

async function claimTasks(count) {
  try {
    await designerTaskService.claimTask(count);
    showToast(`成功接单 ${count} 单`, 'success');
    loadTasks();
  } catch (error) {
    showToast(error.message || '接单失败', 'warn');
  }
}

function search() { pageNum.value = 1; loadTasks(); }
function resetFilters() { filters.value = defaultFilters(); search(); }
function changePage(page) {
  if (page < 1 || page > totalPages.value || page === pageNum.value) return;
  pageNum.value = page;
  loadTasks();
}

async function startDesign(task) {
  if (!canStart(task.status)) return;
  if (task.status === DESIGNER_TASK_STATUS.DESIGN_PENDING) {
    try {
      await designerTaskService.startTask(task.taskId);
      const nextStatus = DESIGNER_TASK_STATUS.DESIGNING;
      tasks.value = tasks.value.map(item => (
        String(item.taskId) === String(task.taskId)
          ? { ...item, status: nextStatus }
          : item
      ));
      if (detailTask.value && String(detailTask.value.taskId) === String(task.taskId)) {
        detailTask.value = { ...detailTask.value, status: nextStatus };
      }
      task.status = nextStatus;
    } catch (error) {
      showToast(error.message || '启动任务失败', 'warn');
      return;
    }
  }
  detailTask.value = null;
  startDesignFromTask({
    ...task,
    id: task.taskId,
    orderId: task.orderNo,
    storeId: resolveStoreId(task),
    status: task.status === DESIGNER_TASK_STATUS.DESIGN_PENDING ? 'waiting' : 'designing',
    songName: task.designRemark || task.productCategory,
  });
}

function resolveStoreId(task) {
  return accessibleStores.value.find(store => store.name === task.storeName)?.id || session.storeId || 's1';
}

async function downloadMaterials(task) {
  const taskKey = getTaskKey(task.taskId);
  const shouldStartTask = task.status === DESIGNER_TASK_STATUS.DESIGN_PENDING;
  downloadingTaskIds.value = new Set(downloadingTaskIds.value).add(taskKey);
  try {
    const payload = await designerTaskService.downloadTaskMaterials({
      taskId: task.taskId,
      orderId: task.orderId,
      shouldStartTask,
    });
    if (shouldStartTask) {
      const nextStatus = DESIGNER_TASK_STATUS.DESIGNING;
      tasks.value = tasks.value.map(item => (
        String(item.taskId) === String(task.taskId)
          ? { ...item, status: nextStatus }
          : item
      ));
      if (detailTask.value && String(detailTask.value.taskId) === String(task.taskId)) {
        detailTask.value = { ...detailTask.value, status: nextStatus };
      }
      task.status = nextStatus;
    }
    const url = payload?.result;
    if (!url) throw new Error(payload?.msg || '未获取到素材下载地址');
    const link = document.createElement('a');
    link.href = url;
    link.download = `order_${task.orderNo}_attachments.zip`;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    showToast(error.message || '下载素材失败', 'warn');
  } finally {
    const next = new Set(downloadingTaskIds.value);
    next.delete(taskKey);
    downloadingTaskIds.value = next;
  }
}

async function confirmReturn({ task, reason }) { returning.value = true; try { await designerTaskService.returnToSales({ orderId: String(task.orderId || task.orderNo), returnReason: reason }); showToast(`订单 ${task.orderNo} 已退回销售`, 'success'); returnTask.value = null; loadTasks(); } catch (error) { showToast(error.message || '退回失败', 'warn'); } finally { returning.value = false; } }
function handleRealtimeUpdate() { loadTasks(); }
onMounted(() => { window.addEventListener('designer-task-updated', handleRealtimeUpdate); loadTasks(); });
onUnmounted(() => window.removeEventListener('designer-task-updated', handleRealtimeUpdate));
</script>

<!-- 任务筛选、详情与退回弹窗均为子组件，公共任务样式必须跨组件生效。 -->
<style src="./tasks-panel.css"></style>
